import { EventEmitter, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Lecture } from '../models/lecture';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HelpersService } from './helpers.service';
import { Tutor, TutorAppointmentRequest, TutorRequest, TutorStudentRequest } from '../models/tutor';
import { Course, FeedType, Group, GroupRequest, Lesson, NotificationRequest, User } from '../models/notification';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public LIMIT_SIZE = 20;
  public onClickSearch: EventEmitter<any> = new EventEmitter();

  constructor(private http: HttpClient,
              private helpersService: HelpersService) { }

  public getOngoingLectures(page: any, filters: any): Observable<Lecture[]> {
    let url = environment.urlApi + '/ext/backoffice/lessons/current?page=' + page;
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key] && filters[key].length) {
          url = url + '&' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.headers).pipe(map(res => {
      return res as Lecture[];
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getTodayLectures(page: any, filters: any): Observable<Lecture[]> {
    let url = environment.urlApi + '/ext/backoffice/lessons/today?page=' + page;
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key] && filters[key].length) {
          url = url + '&' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.headers).pipe(map(res => {
      return res as Lecture[];
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getReport(): any {
    return this.http.get(environment.urlApi + '/ext/backoffice/lessons/report', this.helpersService.fileHeaders).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public login(req: any): any {
    const httpHeaders = new HttpHeaders().set('Authorization', `Basic ${btoa(req.username + ':' + req.password)}`);
    const headers = { headers: httpHeaders };
    return this.http.get(environment.urlApi + '/ext/backoffice/auth', headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getTutorsList(page: any, filters: any): Observable<Tutor[]> {
    let url = environment.urlApi + '/ext/backoffice/tutors?page=' + page;
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key]) {
          url = url + '&' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.headers).pipe(map((res: any) => {
      return res.tutors as Tutor[];
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getTutor(id: string): Observable<Tutor> {
    const url = environment.urlApi + `/ext/backoffice/tutors/${id}`;
    // const url = 'https://reqres.in/api/users';
    return this.http.get(url, this.helpersService.headers).pipe(map((res: any) => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public postTutor(req: TutorRequest): Observable<any> {
    const url = environment.urlApi + '/ext/backoffice/tutors';
    return this.http.post(url, req, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public putTutor(req: TutorRequest): Observable<any> {
    const url = environment.urlApi + `/ext/backoffice/tutors/${req.id}`;
    return this.http.put(url, req, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public deleteTutor(id: number): Observable<any> {
    const url = environment.urlApi + `/ext/backoffice/tutors/${id}`;
    return this.http.delete(url, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  // studenti associati al tutor
  public getTutorStudents(id: string, page: any, filters: any): Observable<any> { // TODO modello Tutor Student
    let url = environment.urlApi + `/ext/backoffice/tutors/${id}/students?page=${page}`;
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key]) {
          url = url + '&' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.headers).pipe(map((res: any) => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getStudentsList(page: any, filters: any): Observable<any> { // TODO modello Student
    let url = environment.urlApi + `/ext/backoffice/students`;
    // let url = 'https://reqres.in/api/users';
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key] && filters[key].length) {
          url = url + '&' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.headers).pipe(map((res: any) => {
      return res as any;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public postAppointment(req: TutorAppointmentRequest, idTutor: number): Observable<any> {
    const url = environment.urlApi + `/ext/backoffice/tutors/${idTutor}/appointments`;
    return this.http.post(url, req, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public putAppointment(req: TutorAppointmentRequest): Observable<any> {
    const url = environment.urlApi + '/ext/backoffice/tutors/appointments/' + req.appointment_id;
    return this.http.put(url, req, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public deleteAppointment(id: number): Observable<any> {
    const url = environment.urlApi + '/ext/backoffice/tutors/appointments/' + id;
    return this.http.delete(url, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public postTutorStudent(req: TutorStudentRequest, idTutor: string): Observable<any> {
    const url = environment.urlApi + '/ext/backoffice/tutors/' + idTutor + '/students';
    return this.http.post(url, req, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public putTutorStudent(req: TutorStudentRequest): Observable<any> {
    const url = environment.urlApi + '/ext/backoffice/tutors/students/' + req.id;
    return this.http.put(url, req, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public deleteTutorStudent(id: number): Observable<any> {
    const url = environment.urlApi + '/ext/backoffice/tutors/students/' + id;
    return this.http.delete(url, this.helpersService.headers).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

   public getNotificationList(page: any, filters: any): Observable<Notification[]> {
    let url = environment.urlApi + '/ext/bo-push-notifications/list?page=' + page;
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key]) {
          url = url + '&' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res.push as Notification[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public createNotification(req: NotificationRequest): Observable<any> {
    const url = environment.urlApi + '/ext/bo-push-notifications/create';
    return this.http.post(url, req, this.helpersService.getHTTPHeader()).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getCustomGroupList(): Observable<Group[]> {
    let url = environment.urlApi + '/ext/bo-push-notifications/custom-groups';
    // if (filters) {
    //   for (const key of Object.keys(filters)) {
    //     if (filters[key]) {
    //       url = url + '&' + key + '=' + filters[key];
    //     }
    //   }
    // }
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as Group[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getRecipientsList(id: number): Observable<any[]> {
    let url = environment.urlApi + `/ext/bo-push-notifications/custom-groups/${id}/recipients`;
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as any[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public createCustomGroup(req: GroupRequest): Observable<any> {
    const url = environment.urlApi + '/ext/bo-push-notifications/custom-groups';
    return this.http.post(url, req, this.helpersService.getHTTPHeader()).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public editCustomGroup(id:number, req: GroupRequest): Observable<any> {
    const url = environment.urlApi + `/ext/bo-push-notifications/custom-groups/${id}`;
    return this.http.put(url, req, this.helpersService.getHTTPHeader()).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public updateRecipients(id:number, req: any): Observable<any> {
    const url = environment.urlApi + `/ext/bo-push-notifications/custom-groups/${id}/recipients`;
    return this.http.put(url, req, this.helpersService.getHTTPHeader()).pipe(map(res => {
      return res;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getCoursesList(): Observable<Course[]> {
    let url = environment.urlApi + '/ext/bo-push-notifications/courses';
    // if (filters) {
    //   for (const key of Object.keys(filters)) {
    //     if (filters[key]) {
    //       url = url + '&' + key + '=' + filters[key];
    //     }
    //   }
    // }
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as Course[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getCourseYearsList(cdsCode :string): Observable<number[]> {
    let url = environment.urlApi + `/ext/bo-push-notifications/courses/${cdsCode}/years`;
    // if (filters) {
    //   for (const key of Object.keys(filters)) {
    //     if (filters[key]) {
    //       url = url + '&' + key + '=' + filters[key];
    //     }
    //   }
    // }
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as number[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getUsersList(filters: any): Observable<User[]> {
    let url = environment.urlApi + '/ext/bo-push-notifications/users';
    if (filters) {
      for (const key of Object.keys(filters)) {
        if (filters[key]) {
          url = url + '?' + key + '=' + filters[key];
        }
      }
    }
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as User[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getLessonList(filters: string): Observable<Lesson[]> {
    let url = environment.urlApi + `/ext/bo-push-notifications/lessons?${filters}`;
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as Lesson[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getFeedList(text: string, type: FeedType): Observable<any[]> {
    let url = environment.urlApi + `/ext/bo-push-notifications/feeds?search=${text}&feed_type=${type}`;
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res as any[] ;
    }), catchError(error => {
      return throwError(error);
    }));
  }

  public getDeliveriesList(idNotification: number, page: any): Observable<User[]> {
    let url = `${environment.urlApi}/ext/bo-push-notifications/${idNotification}/deliveries?page=${page}`;
    return this.http.get(url, this.helpersService.getHTTPHeader()).pipe(map((res: any) => {
      return res.deliveries as User[];
    }), catchError(error => {
      return throwError(error);
    }));
  }


}
